import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logoIcon from '../assests/logo-icon.png';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  // Helper function to navigate user according to role
  const redirectByRole = async (userRole) => {
    try {
      const normalizedRole = userRole?.toLowerCase();
      switch (normalizedRole) {
        case 'super_admin':
        case 'admin':
          navigate('/dashboard/admin');
          break;
        case 'principal':
          navigate('/dashboard/principal');
          break;
        case 'accountant':
          navigate('/dashboard/accountant');
          break;
        case 'teacher':
          navigate('/dashboard/teacher');
          break;
        case 'librarian':
          navigate('/dashboard/librarian');
          break;
        case 'parent':
          navigate('/parent/dashboard');
          break;
        case 'student':
          navigate('/dashboard/student');
          break;
        default:
          navigate('/dashboard/admin');
          break;
      }
    } catch (err) {
      console.error('Role redirection error:', err);
      navigate('/dashboard/admin');
    }
  };

  // Function to handle login form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await login(email, password);

      if (result.success) {
        await redirectByRole(result.user?.role);
      } else {
        setError(result.error || 'Invalid credentials. Please try again.');
      }
    } catch (err) {
      console.error('Login submit handler error:', err);
      setError('An unexpected error occurred during login.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4 py-12">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
        
        {/* Header Branding */}
        <div className="bg-gradient-to-r from-blue-700 to-primary p-8 text-center text-white flex flex-col items-center justify-center">
          <img src={logoIcon} alt="MarkazSchool Pro Icon" className="h-16 w-auto mx-auto drop-shadow-md object-contain" />
          <h1 className="text-2xl font-black text-white tracking-tight mt-2">
            MarkazSchool <span className="text-amber-400">Pro</span>
          </h1>
          <p className="text-blue-100 text-xs font-medium mt-1 whitespace-nowrap">Official School Management Portal • Powered by Waleed Technologies</p>
        </div>

        {/* Login Form */}
        <div className="p-8">
          <h2 className="text-xl font-semibold text-slate-800 mb-6 text-center">
            Sign In to Your Account
          </h2>

          {error && (
            <div className="mb-5 p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-500 mt-0.5 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-sm text-red-700 leading-snug">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-slate-700 mb-1.5"
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@school.com"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition text-slate-800 placeholder-slate-400"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-slate-700 mb-1.5"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition text-slate-800 placeholder-slate-400"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary hover:bg-blue-600 text-white font-medium py-3 rounded-xl transition duration-200 shadow-md hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>Signing in...</span>
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>

        {/* Vendor Footer Attribution */}
        <div className="bg-slate-50 border-t border-slate-100 p-4 text-center text-xs text-slate-500 font-medium whitespace-nowrap">
          © {new Date().getFullYear()} <strong>MarkazSchool Pro</strong> • Powered & Maintained by <strong className="text-purple-700">Waleed Technologies</strong>
        </div>
      </div>
    </div>
  );
};

export default Login;
